import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loginDirect } from '../services/authService';
import { useRegistration } from '../store/RegistrationContext';
import { registrationActionTypes } from '../store/registrationReducer';

export default function LoginPage() {
  const { dispatch } = useRegistration();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  const canSubmit = useMemo(() => email.trim().length > 0 && password.trim().length > 0, [email, password]);

  const nextPath = location.state?.from || '/students';

  const onLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    setLoading(true);

    const result = await loginDirect({ email: email.trim(), password: password.trim() });
    setLoading(false);

    if (!result.success) {
      setAuthError(result.error);
      return;
    }

    dispatch({
      type: registrationActionTypes.LOGIN_SUCCESS,
      payload: {
        email: email.trim(),
        entity: process.env.REACT_APP_ENTITY || 'RMS',
        userData: result.user
      }
    });

    navigate(nextPath);
  };

  const onGuest = () => {
    navigate('/guest-login', { state: { from: nextPath } });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fadeIn overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(0,0,0,.05) 35px, rgba(0,0,0,.05) 70px)'
          }}
        />
      </div>

      <div className="relative bg-white rounded-2xl shadow-[0_20px_70px_rgba(0,0,0,0.08)] max-w-md w-full p-10 animate-slideInUp border border-gray-100">
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-black to-transparent" />

        <div className="text-center mb-10 relative">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-black rounded-2xl shadow-xl mb-5 transform hover:scale-105 transition-all duration-300">
            <i className="fa-solid fa-user text-white text-2xl" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2 tracking-tight">Welcome Back</h2>
          <p className="text-gray-500 text-sm">Sign in to continue</p>
        </div>

        <form onSubmit={onLogin} className="space-y-6">
          <div className="relative">
            <input
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-4 bg-white border-2 border-gray-200 rounded-xl focus:border-black focus:ring-2 focus:ring-gray-100 outline-none transition-all duration-200 text-base text-gray-900 placeholder-gray-400"
              placeholder="Email / User ID"
              disabled={loading}
            />
          </div>

          <div className="relative">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-4 bg-white border-2 border-gray-200 rounded-xl focus:border-black focus:ring-2 focus:ring-gray-100 outline-none transition-all duration-200 text-base text-gray-900 placeholder-gray-400"
              placeholder="Password"
              disabled={loading}
            />
          </div>

          {authError ? (
            <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-4 animate-shake">
              <div className="flex items-center gap-3">
                <i className="fa-solid fa-triangle-exclamation text-red-500" />
                <p className="text-red-700 text-sm font-medium">{authError}</p>
              </div>
            </div>
          ) : null}

          <div className="flex gap-3 pt-6">
            <button
              type="button"
              onClick={() => navigate('/retreat')}
              disabled={loading}
              className="flex-1 bg-white border-2 border-gray-200 text-gray-700 font-semibold py-4 px-4 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={!canSubmit || loading}
              className="flex-1 bg-black text-white font-bold py-4 px-4 rounded-xl hover:bg-gray-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </div>

          <div className="relative py-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500 font-medium">New here?</span>
            </div>
          </div>

          <button
            type="button"
            onClick={onGuest}
            className="w-full py-4 px-6 bg-gray-900 text-white font-semibold rounded-xl hover:bg-gray-800 transition-all duration-200"
          >
            Continue as Guest
          </button>
        </form>
      </div>
    </div>
  );
}
